import { LoaderIcon } from '@/app/features/icons/Loader'
import { DriverCalendarModel } from '@/app/models/calendar/driverCalendarModel'
import { DayType } from '@/app/constants/dayType'
import { FaIcon, faUserSolid } from '@/lib/fa'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import YearlyActivityCalendar from '@/modules/drivers/tabs/activities/components/charts/yearly/YearlyActivityCalendar'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import React, { useEffect } from 'react'
import FlexView from '@/modules/shared/components/FlexView'

const ABSENCE_DAY_TYPES = new Set(
  Object.entries(DayType)
    .filter(([key]) => key.startsWith('N'))
    .map(([, val]) => Number(val)),
)

const countAbsenceDays = (calendar: DriverCalendarModel): number =>
  calendar.dayActivities
    .flat()
    .filter((day) => ABSENCE_DAY_TYPES.has(day.dayType)).length

const ActivityModal = ({
  close,
  driverId,
  companyId,
  year,
  driverName,
}: {
  close: () => void
  driverId: string
  companyId: string
  year: number
  driverName: string
}) => {
  const { t } = useLocalize()
  const { getCalendar } = useCalendarService()

  const [calendarData, setCalendarData] =
    React.useState<DriverCalendarModel | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const fetchCalendar = async () => {
    setIsLoading(true)
    try {
      const data = await getCalendar({ companyId, driverId, year })
      setCalendarData(data)
    } catch (error) {
      console.error('Error fetching calendar:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (companyId && driverId) {
      fetchCalendar()
    }
  }, [companyId, driverId])
  return (
    <ModalLayout>
      <div className="tw-flex tw-h-[95vh] tw-w-[95vw] tw-flex-col tw-overflow-hidden tw-rounded tw-bg-white">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              Aktywność kierowcy
            </p>
            <div
              key={driverName}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-base-300 tw-px-2 tw-py-1"
            >
              <FaIcon icon={faUserSolid} className="tw-h-3 tw-w-3" />
              <p className="tw-text-xs tw-text-content-base">{driverName}</p>
            </div>
          </div>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => close()}
          />
        </div>
        <div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-auto tw-p-4">
          {isLoading && !calendarData ? (
            <div className="tw-grid tw-h-full tw-w-full tw-place-items-center">
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                <LoaderIcon className="tw-h-16 tw-w-16 tw-animate-spin tw-text-primary" />
                <span className="tw-text-gray-600">
                  {t('driverModule.loading-calendar')}
                </span>
              </div>
            </div>
          ) : calendarData ? (
            <YearlyActivityCalendar
              showViolations={false}
              showTrips={false}
              calendar={calendarData}
              companyId={companyId}
              driverId={Number(driverId)}
              isLoading={isLoading}
              refreshCalendar={fetchCalendar}
            />
          ) : (
            <div className="tw-grid tw-h-full tw-w-full tw-place-items-center tw-p-4">
              <FlexView vAlignContent="center" hAlignContent="center">
                <span style={{ color: '#97A2B3' }} className="text-xl tw-my-4">
                  {t('driverCalendar.no-data')}
                </span>
              </FlexView>
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  )
}

export default ActivityModal
